/**
 * Insight Card - Composant réutilisable pour afficher un insight
 */

import React from 'react';
import {
  TrendingUp,
  AlertCircle,
  Sparkles,
  BarChart3,
  Database,
  Bookmark,
  CheckCircle,
  Eye,
  Download,
  ExternalLink
} from 'lucide-react';
import { BigDataInsight } from '../../services/BigDataInsightsService';

interface InsightCardProps {
  insight: BigDataInsight;
  onBookmark?: () => void;
  onValidate?: () => void;
  onNavigate?: () => void;
  onExport?: () => void;
  compact?: boolean;
}

const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  onBookmark,
  onValidate,
  onNavigate,
  onExport,
  compact = false
}) => {
  // Icon basé sur le type
  const getTypeIcon = () => {
    switch (insight.type) {
      case 'pattern': return TrendingUp;
      case 'anomaly': return AlertCircle;
      case 'correlation': return BarChart3;
      case 'trend': return TrendingUp;
      case 'statistical': return Database;
      default: return Sparkles;
    }
  };

  // Couleur basée sur la catégorie
  const getCategoryColor = () => {
    switch (insight.category) {
      case 'critical': return 'red';
      case 'high_value': return 'purple';
      case 'medium_value': return 'blue';
      case 'low_value': return 'gray';
      case 'warning': return 'yellow';
      case 'info': return 'green';
      default: return 'gray';
    }
  };

  // Label catégorie
  const getCategoryLabel = () => {
    switch (insight.category) {
      case 'critical': return 'CRITIQUE';
      case 'high_value': return 'HAUTE VALEUR';
      case 'medium_value': return 'VALEUR MOYENNE';
      case 'low_value': return 'FAIBLE VALEUR';
      case 'warning': return 'AVERTISSEMENT';
      case 'info': return 'INFO';
      default: return 'AUTRE';
    }
  };

  const TypeIcon = getTypeIcon();
  const color = getCategoryColor();
  const categoryLabel = getCategoryLabel();
  const confidencePercent = Math.round((insight.confidence || 0) * 100);

  if (compact) {
    return (
      <div className={`bg-theme-surface/50 rounded-lg p-3 border-l-4 border-${color}-500 hover:bg-theme-surface transition-colors cursor-pointer`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <TypeIcon className={`w-4 h-4 text-${color}-400 flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-theme-primary truncate">{insight.title}</h4>
              <p className="text-xs text-theme-secondary mt-1 line-clamp-1">{insight.description}</p>
            </div>
          </div>
          <div className={`px-2 py-0.5 bg-${color}-500/20 text-${color}-400 rounded text-xs font-medium whitespace-nowrap ml-2`}>
            {confidencePercent}%
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-theme-surface/30 border border-theme rounded-2xl p-6 hover:border-${color}-500 transition-all hover:shadow-lg`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4 flex-1">
          {/* Icon */}
          <div className={`w-12 h-12 bg-${color}-500/20 rounded-xl flex items-center justify-center flex-shrink-0`}>
            <TypeIcon className={`w-6 h-6 text-${color}-400`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <span className={`px-3 py-1 bg-${color}-500/20 text-${color}-400 rounded-full text-xs font-bold uppercase`}>
                {categoryLabel}
              </span>
              <span className="text-xs text-theme-secondary capitalize">{insight.type}</span>
            </div>
            <h3 className="text-lg font-semibold text-theme-primary mb-2">
              {insight.title}
            </h3>
            <p className="text-theme-secondary text-sm leading-relaxed">
              {insight.description}
            </p>
          </div>
        </div>

        {/* Confidence Badge */}
        <div className="flex flex-col items-end space-y-1 ml-4">
          <div className={`px-3 py-1 bg-${color}-500/20 text-${color}-400 rounded-full text-sm font-bold`}>
            {confidencePercent}%
          </div>
          <span className="text-xs text-theme-secondary">confiance</span>
        </div>
      </div>

      {/* Metadata Row */}
      <div className="flex items-center space-x-4 text-xs text-theme-secondary mb-4">
        {insight.row_start !== undefined && insight.row_start !== null && (
          <span>Lignes: {insight.row_start.toLocaleString()} - {insight.row_end?.toLocaleString() || 'N/A'}</span>
        )}
        {insight.page_number !== undefined && insight.page_number !== null && (
          <span>Page {insight.page_number}</span>
        )}
        {insight.sql_result_count !== undefined && insight.sql_result_count !== null && (
          <span>{insight.sql_result_count} résultats</span>
        )}
        {insight.discovered_by && (
          <span className="capitalize">· {insight.discovered_by.replace('_', ' ')}</span>
        )}
        <span>· {new Date(insight.discovered_at).toLocaleString()}</span>
      </div>

      {/* Metrics */}
      {(insight.impact_score !== undefined || insight.occurrence_count) && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {insight.impact_score !== undefined && insight.impact_score !== null && (
            <div className="bg-theme-surface/50 rounded-lg p-3">
              <div className="text-xs text-theme-secondary mb-1">Impact</div>
              <div className="text-lg font-bold text-theme-primary">
                {Math.round(insight.impact_score * 100)}%
              </div>
            </div>
          )}
          {insight.occurrence_count && insight.occurrence_count !== null && (
            <div className="bg-theme-surface/50 rounded-lg p-3">
              <div className="text-xs text-theme-secondary mb-1">Occurrences</div>
              <div className="text-lg font-bold text-theme-primary">
                {insight.occurrence_count.toLocaleString()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {insight.tags && insight.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {insight.tags.map((tag, idx) => (
            <span
              key={idx}
              className="px-2 py-1 bg-theme-surface text-theme-secondary rounded text-xs"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-theme">
        <div className="flex items-center space-x-2">
          {onBookmark && (
            <button
              onClick={onBookmark}
              className={`p-2 rounded-lg transition-colors ${
                insight.is_bookmarked
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-theme-surface text-theme-secondary hover:bg-theme'
              }`}
              title="Bookmark"
            >
              <Bookmark className="w-4 h-4" />
            </button>
          )}
          
          {onValidate && (
            <button
              onClick={onValidate}
              className={`p-2 rounded-lg transition-colors ${
                insight.is_validated
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-theme-surface text-theme-secondary hover:bg-theme'
              }`}
              title="Valider"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}

          {onNavigate && insight.row_start !== undefined && insight.row_start !== null && (
            <button
              onClick={onNavigate}
              className="px-3 py-2 bg-theme-surface text-theme-primary rounded-lg hover:bg-theme transition-colors inline-flex items-center space-x-2 text-sm"
              title="Voir dans les données"
            >
              <Eye className="w-4 h-4" />
              <span>Voir</span>
            </button>
          )}
        </div>

        {onExport && (
          <button
            onClick={onExport}
            className="p-2 bg-theme-surface text-theme-secondary hover:bg-theme rounded-lg transition-colors"
            title="Exporter"
          >
            <Download className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default InsightCard;



